// src/hooks/useNavigationHandler.js

import { useCallback } from 'react';

/**
 * Hook personalizado para manejar la navegación hacia atrás en la encuesta
 * @param {Object} params - Parámetros del hook
 * @returns {Object} Handlers de navegación
 */
export const useNavigationHandler = ({
  nameStep,
  activeStepper,
  activeDemographicStep,
  activeStep,
  totalDemographicSteps,
  handlePreviousAnswer,
  setActiveDemographicStep,
  setActiveStep,
  setSubmitAttempted,
  setDemographicSelections,
}) => {
  /**
   * Verifica si estamos en el paso de demografía
   */
  const isInDemographicStep = useCallback(() => {
    return nameStep && nameStep[activeStepper] === 'Datos demográficos';
  }, [nameStep, activeStepper]);

  /**
   * Verifica si estamos en el primer paso de la encuesta
   */
  const isFirstSurveyStep = useCallback(() => {
    return activeStep === 0;
  }, [activeStep]);

  /**
   * Verifica si el paso anterior es demografía
   */
  const isPreviousStepDemographic = useCallback(() => {
    return (
      activeStepper > 0 &&
      nameStep &&
      nameStep[activeStepper - 1] === 'Datos demográficos'
    );
  }, [activeStepper, nameStep]);

  /**
   * CASO 1: Navegar hacia atrás dentro de los pasos demográficos
   */
  const handleBackWithinDemographics = useCallback(() => {
    setActiveDemographicStep((prev) => prev - 1);
    setSubmitAttempted(false);
  }, [setActiveDemographicStep, setSubmitAttempted]);

  /**
   * CASO 2: Volver del primer paso demográfico al paso anterior
   */
  const handleBackFromFirstDemographicStep = useCallback(() => {
    if (handlePreviousAnswer) {
      handlePreviousAnswer();
    }
    setSubmitAttempted(false);
  }, [handlePreviousAnswer, setSubmitAttempted]);

  /**
   * CASO 3: Volver desde la encuesta hacia los demográficos
   */
  const handleBackToLastDemographicStep = useCallback(() => {
    if (handlePreviousAnswer) {
      // Restaurar selecciones desde localStorage
      const savedSelections = localStorage.getItem('demographicSelections');
      if (savedSelections) {
        try {
          setDemographicSelections(JSON.parse(savedSelections));
        } catch (error) {
          console.error('Error parsing saved demographic selections:', error);
        }
      }

      // Configurar el último paso de demográficos
      const totalDemographicStepsCount = totalDemographicSteps();
      setActiveDemographicStep(totalDemographicStepsCount - 1);
      setActiveStep(0);

      handlePreviousAnswer();
    }
    setSubmitAttempted(false);
  }, [
    handlePreviousAnswer,
    totalDemographicSteps,
    setActiveDemographicStep,
    setActiveStep,
    setSubmitAttempted,
    setDemographicSelections,
  ]);

  /**
   * CASO 4: Navegación normal dentro de los pasos de la encuesta
   */
  const handleBackWithinSurvey = useCallback(() => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
      setSubmitAttempted(false);
    }
  }, [activeStep, setActiveStep, setSubmitAttempted]);

  /**
   * Maneja el botón "Atrás" con lógica de navegación completa
   */
  const handleBack = useCallback(() => {
    // CASO 1: Estamos en demografía y NO es el primer paso demográfico
    if (isInDemographicStep() && activeDemographicStep > 0) {
      handleBackWithinDemographics();
      return;
    }

    // CASO 2: Estamos en demografía en el primer paso demográfico
    if (isInDemographicStep() && activeDemographicStep === 0) {
      handleBackFromFirstDemographicStep();
      return;
    }

    // CASO 3: Estamos en la ENCUESTA en el primer paso y queremos volver a demografía
    if (
      !isInDemographicStep() &&
      isFirstSurveyStep() &&
      isPreviousStepDemographic()
    ) {
      handleBackToLastDemographicStep();
      return;
    }

    // CASO 4: Comportamiento normal dentro de las preguntas de la encuesta
    handleBackWithinSurvey();
  }, [
    isInDemographicStep,
    activeDemographicStep,
    isFirstSurveyStep,
    isPreviousStepDemographic,
    handleBackWithinDemographics,
    handleBackFromFirstDemographicStep,
    handleBackToLastDemographicStep,
    handleBackWithinSurvey,
  ]);

  return {
    handleBack,
  };
};